import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Star, Award, Eye, EyeOff, Trash2, MessageSquareReply, Save } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Stars } from '../pages/ReviewsPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewRow = ({ r, onPatch, onDelete }) => {
  const [reply, setReply] = useState(r.reply || '');
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`admin-review-${r.id}`} className={`px-5 py-4 ${r.is_hidden ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Stars value={r.rating} size={14} />
            <span className="font-medium text-[14px]">{r.name}</span>
            <span className="text-[11.5px] text-neutral-500">{r.customer_email} · {new Date(r.created_at).toLocaleDateString()}</span>
            {r.is_featured && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10.5px] font-semibold"><Award size={11} /> Featured</span>}
            {r.is_hidden && <span className="rounded-full bg-neutral-200 text-neutral-600 px-2 py-0.5 text-[10.5px]">Hidden</span>}
          </div>
          {r.title && <div className="mt-1.5 font-semibold text-[14px]">{r.title}</div>}
          <p className="mt-1 text-[13.5px] text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{r.text}</p>
          {r.reply && !open && <p className="mt-2 text-[12.5px] text-emerald-700 dark:text-emerald-400 whitespace-pre-wrap"><b>Your reply:</b> {r.reply}</p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`admin-review-feature-${r.id}`} onClick={() => onPatch(r.id, { is_featured: !r.is_featured })} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-[12px] ${r.is_featured ? 'border-amber-400 text-amber-700' : 'border-neutral-200 dark:border-neutral-700'}`}><Award size={13} /> {r.is_featured ? 'Unfeature' : 'Feature'}</button>
          <button data-testid={`admin-review-hide-${r.id}`} onClick={() => onPatch(r.id, { is_hidden: !r.is_hidden })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[12px]">{r.is_hidden ? <Eye size={13} /> : <EyeOff size={13} />} {r.is_hidden ? 'Show' : 'Hide'}</button>
          <button data-testid={`admin-review-reply-toggle-${r.id}`} onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[12px]"><MessageSquareReply size={13} /> Reply</button>
          <button data-testid={`admin-review-delete-${r.id}`} onClick={() => onDelete(r)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      </div>
      {open && (
        <div className="mt-3 flex items-end gap-2">
          <textarea data-testid={`admin-review-reply-input-${r.id}`} rows={2} maxLength={1000} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Public reply shown under the review (leave empty to remove)"
            className="flex-1 bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13px] outline-none focus:border-neutral-400 resize-none" />
          <button data-testid={`admin-review-reply-save-${r.id}`} onClick={async () => { if (await onPatch(r.id, { reply })) setOpen(false); }} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-[13px] font-semibold"><Save size={13} /> Save</button>
        </div>
      )}
    </div>
  );
};

const ReviewsPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setRows((await axios.get(`${API}/admin/reviews`, { headers })).data); } catch { setErr('Failed to load reviews'); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try { const { data } = await axios.patch(`${API}/admin/reviews/${id}`, body, { headers }); setRows(prev => prev.map(r => (r.id === id ? data : r))); return true; }
    catch (e) { setErr(e?.response?.data?.detail || 'Update failed'); return false; }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.name}?`)) return;
    try { await axios.delete(`${API}/admin/reviews/${r.id}`, { headers }); setRows(prev => prev.filter(x => x.id !== r.id)); } catch { setErr('Delete failed'); }
  };
  const visible = rows.filter(r => !r.is_hidden);
  const avg = visible.length ? (visible.reduce((a, r) => a + r.rating, 0) / visible.length).toFixed(1) : '—';

  return (
    <div className="space-y-4" data-testid="reviews-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Star size={20} className="text-neutral-400" /> Reviews</h2>
          <p className="text-[12.5px] text-neutral-500">Store reviews appear instantly on /reviews. Hide, feature or reply publicly.</p>
        </div>
        <div className="text-[13px] text-neutral-600 dark:text-neutral-300" data-testid="admin-reviews-stats"><b>{avg}</b> average · {visible.length} visible · {rows.length - visible.length} hidden</div>
      </div>
      {err && <div className="text-red-600 text-[13px]">{err}</div>}
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] divide-y divide-neutral-100 dark:divide-neutral-800">
        {rows.length === 0 ? <div className="p-6 text-center text-[13.5px] text-neutral-500">No reviews yet.</div>
          : rows.map(r => <ReviewRow key={r.id} r={r} onPatch={patch} onDelete={remove} />)}
      </div>
    </div>
  );
};

export default ReviewsPanel;
